import { Card, H5, Intent, Tag, Menu, MenuItem, Icon } from "@blueprintjs/core";
import { ContextMenu2 } from "@blueprintjs/popover2";
import { useCallback, useContext } from "react";
import { Droppable, Draggable } from "react-beautiful-dnd";
import { InstructionTo, ListState } from "src/models/TemplateCreationList";
import { UserInput } from "src/models/UserInputs";
import { SourceDialogCurrentProjectContext } from "src/utils/context";
import { Dictionary } from "src/utils/utils";

type Props = {
    lists: ListState;
    setLists: React.Dispatch<React.SetStateAction<ListState>>;
    list: InstructionTo[];
    setList: (listName: keyof ListState, newList: InstructionTo[]) => void;
    processInputs: Dictionary<UserInput>;
    setProcessInputs: React.Dispatch<React.SetStateAction<Dictionary<UserInput>>>;
};

export default function InputManuallySet({ lists, setLists, list, setList, processInputs, setProcessInputs }: Props) {
    const [currentProject] = useContext(SourceDialogCurrentProjectContext);
    const handleAddToProcessInputs = useCallback(
        (name: string) => {
            if (currentProject) {
                const userInputs: Dictionary<UserInput> = currentProject.userInputs;
                const input = userInputs[name];
                const copy = { ...processInputs };
                copy[name] = input;

                setProcessInputs(copy);
            }
        },
        [currentProject, processInputs, setProcessInputs]
    );

    const getIntent = useCallback(
        (isDragging, name) => {
            if (isDragging) return Intent.PRIMARY;
            if (Object.keys(processInputs).includes(name)) return Intent.SUCCESS;
            return Intent.NONE;
        },
        [processInputs]
    );

    const isProcessInput = useCallback(
        (name: string) => {
            return Object.keys(processInputs).includes(name);
        },
        [processInputs]
    );

    const handleRemoveProcessInput = useCallback(
        (instructionTo: InstructionTo, index: number) => {
            const { [instructionTo.original]: remove, ...newProcessInputs } = processInputs;
            setProcessInputs(newProcessInputs);
            const newList = [...list];
            const mapsTo = newList[index].mapsTo;

            // remove user input mapping if it is mapped to a
            // deleted process input
            if (mapsTo && !newProcessInputs[mapsTo]) {
                newList[index].mapsTo = undefined;
                setList("manual", newList);
            }
        },
        [list, processInputs, setList, setProcessInputs]
    );

    return (
        <Card className="instructions-card">
            <H5>Manually Set</H5>
            <Droppable droppableId={`manual`}>
                {provided => (
                    <ul style={{ listStyle: "none" }} {...provided.droppableProps} ref={provided.innerRef}>
                        {lists.manual.map((instructionTo, index) => {
                            return (
                                <Draggable
                                    key={`manual_${instructionTo.original}_${index}`}
                                    draggableId={`manual_${instructionTo.original}_${index}`}
                                    index={index}
                                >
                                    {(provided, snapshot) => (
                                        <li
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            ref={provided.innerRef}
                                        >
                                            <ContextMenu2
                                                content={
                                                    <Menu>
                                                        <MenuItem
                                                            key={`add_pro_${instructionTo.original}_${index}`}
                                                            icon="new-object"
                                                            text="Add to Process Inputs"
                                                            onClick={() =>
                                                                handleAddToProcessInputs(instructionTo.original)
                                                            }
                                                        />
                                                        <MenuItem
                                                            key={`map_pro_${instructionTo.original}_${index}`}
                                                            icon="new-link"
                                                            text="Map to a Process Input"
                                                            disabled={Object.keys(processInputs).length === 0}
                                                        >
                                                            {Object.keys(processInputs).map(key => (
                                                                <MenuItem
                                                                    key={`user_input_${key}`}
                                                                    text={key}
                                                                    onClick={() => {
                                                                        const newList = [...list];
                                                                        newList[index].mapsTo = key;
                                                                        setList("manual", newList);
                                                                    }}
                                                                />
                                                            ))}
                                                        </MenuItem>
                                                        <MenuItem
                                                            key={`remove_pro_map_${instructionTo.original}_${index}`}
                                                            icon="cross"
                                                            text="Remove Mapping"
                                                            intent={Intent.DANGER}
                                                            disabled={instructionTo.mapsTo ? false : true}
                                                            onClick={() => {
                                                                const newList = [...list];
                                                                newList[index].mapsTo = undefined;
                                                                setList("manual", newList);
                                                            }}
                                                        ></MenuItem>
                                                        <MenuItem
                                                            key={`remove_pro_${instructionTo.original}_${index}`}
                                                            icon="cross"
                                                            text="Remove From Processs Inputs"
                                                            intent={Intent.DANGER}
                                                            disabled={!isProcessInput(instructionTo.original)}
                                                            onClick={() =>
                                                                handleRemoveProcessInput(instructionTo, index)
                                                            }
                                                        ></MenuItem>
                                                    </Menu>
                                                }
                                            >
                                                <div className="dependecy-tag">
                                                    <Tag
                                                        key={`manual_tag_${instructionTo.original}_${index}`}
                                                        intent={getIntent(snapshot.isDragging, instructionTo.original)}
                                                    >
                                                        {instructionTo.original}
                                                    </Tag>
                                                    {instructionTo.mapsTo && (
                                                        <>
                                                            <Icon icon="arrow-right" intent={Intent.PRIMARY} />
                                                            <Tag
                                                                key={`tag_${instructionTo.mapsTo}_${index}`}
                                                                intent={
                                                                    snapshot.isDragging ? Intent.PRIMARY : Intent.NONE
                                                                }
                                                            >
                                                                {instructionTo.mapsTo}
                                                            </Tag>
                                                        </>
                                                    )}
                                                </div>
                                            </ContextMenu2>
                                        </li>
                                    )}
                                </Draggable>
                            );
                        })}
                        {provided.placeholder}
                    </ul>
                )}
            </Droppable>
        </Card>
    );
}
